import { useState, useEffect } from 'react';
import { API_BASE_URL } from '../../api';

// Static list for the admin-user permission checkboxes — mirrors the
// dashboard's module groupings.
export const AVAILABLE_MODULES = [
  { id: 'dashboard', name: 'Dashboard', description: 'View dashboard and analytics' },
  { id: 'operations', name: 'Operations', description: 'Manage orders, delivery, and workflows' },
  { id: 'products', name: 'Products', description: 'Manage inventory and products' },
  { id: 'financial', name: 'Financial', description: 'Manage payments, refunds, and reports' },
  { id: 'communications', name: 'Communications', description: 'Manage notifications, emails, SMS' },
  { id: 'analytics', name: 'Analytics', description: 'View analytics and reports' },
  { id: 'security', name: 'Security', description: 'Manage admin users and permissions' },
  { id: 'settings', name: 'Settings', description: 'System settings and configuration' },
];

const emptyAdminForm = () => ({
  username: '', email: '', fullName: '', phone: '', password: '',
  role: 'admin', isActive: true, permissions: [],
});

/** Owns Admin Users (with the Add/Edit modal), User Audits, and Activity Logs. */
export default function useSecurity({ activeTab, showNotification }) {
  const [adminUsers, setAdminUsers] = useState([]);
  const [loadingAdminUsers, setLoadingAdminUsers] = useState(false);
  const [showAddAdminModal, setShowAddAdminModal] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [adminForm, setAdminForm] = useState(emptyAdminForm());

  const [userAudits, setUserAudits] = useState([]);
  const [loadingUserAudits, setLoadingUserAudits] = useState(false);
  const [auditFilters, setAuditFilters] = useState({ adminId: '', action: '', startDate: '', endDate: '', status: '' });

  const [activityLogs, setActivityLogs] = useState([]);
  const [loadingActivityLogs, setLoadingActivityLogs] = useState(false);

  const fetchAdminUsers = async () => {
    setLoadingAdminUsers(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/admin/security/users`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setAdminUsers(data.users || []);
      }
    } catch (error) {
      console.error('Error fetching admin users:', error);
      showNotification('Failed to fetch admin users', 'error');
    } finally {
      setLoadingAdminUsers(false);
    }
  };

  const fetchUserAudits = async () => {
    setLoadingUserAudits(true);
    try {
      const token = localStorage.getItem('adminToken');
      const params = new URLSearchParams(auditFilters);
      const response = await fetch(`${API_BASE_URL}/api/admin/security/audits?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setUserAudits(data.audits || []);
      }
    } catch (error) {
      console.error('Error fetching user audits:', error);
      showNotification('Failed to fetch user audits', 'error');
    } finally {
      setLoadingUserAudits(false);
    }
  };

  const fetchActivityLogs = async () => {
    setLoadingActivityLogs(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/admin/security/activity-logs`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setActivityLogs(data.logs || []);
      }
    } catch (error) {
      console.error('Error fetching activity logs:', error);
      showNotification('Failed to fetch activity logs', 'error');
    } finally {
      setLoadingActivityLogs(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'admin-users') {
      fetchAdminUsers();
    } else if (activeTab === 'user-audits') {
      fetchUserAudits();
    } else if (activeTab === 'activity-logs') {
      fetchActivityLogs();
    }
  }, [activeTab, auditFilters]);

  const openAddAdminModal = () => {
    setEditingAdmin(null);
    setAdminForm(emptyAdminForm());
    setShowAddAdminModal(true);
  };

  const openEditAdminModal = (user) => {
    setEditingAdmin(user);
    setAdminForm({
      username: user.username,
      email: user.email,
      fullName: user.fullName,
      phone: user.phone || '',
      password: '',
      role: user.role || 'admin',
      isActive: user.isActive,
      permissions: Array.isArray(user.permissions) ? user.permissions : (user.permissions ? JSON.parse(user.permissions) : []),
    });
    setShowAddAdminModal(true);
  };

  const closeAdminModal = () => setShowAddAdminModal(false);

  const handleAdminFormSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('adminToken');
      const url = editingAdmin
        ? `${API_BASE_URL}/api/admin/security/users/${editingAdmin.id}`
        : `${API_BASE_URL}/api/admin/security/users`;
      const method = editingAdmin ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(adminForm),
      });

      if (response.ok) {
        showNotification(`Admin user ${editingAdmin ? 'updated' : 'created'} successfully`, 'success');
        setShowAddAdminModal(false);
        fetchAdminUsers();
      } else {
        const error = await response.json();
        showNotification(error.error || 'Failed to save admin user', 'error');
      }
    } catch (error) {
      showNotification('Network error', 'error');
    }
  };

  return {
    adminUsers, loadingAdminUsers,
    showAddAdminModal, editingAdmin, adminForm, setAdminForm,
    openAddAdminModal, openEditAdminModal, closeAdminModal, handleAdminFormSubmit,
    userAudits, loadingUserAudits, auditFilters, setAuditFilters,
    activityLogs, loadingActivityLogs,
  };
}
